import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { Typography, Stack, TextField, Card, CardContent, Link as MuiLink, Chip, Avatar, Divider, Button, useMediaQuery, useTheme, } from '@mui/material';
import Grid from '@mui/material/Grid2';
import proxiesData from './api.json';
const proxies = proxiesData;
export const HomePage = () => {
    const [query, setQuery] = useState('');
    const [selected, setSelected] = useState(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q)
            return proxies;
        return proxies.filter((p) => {
            var _a, _b, _c, _d, _e, _f, _g;
            const haystacks = [
                p.name,
                p.title,
                p.homepage,
                p.hostname,
                ...(((_b = (_a = p.metadata) === null || _a === void 0 ? void 0 : _a.titles) !== null && _b !== void 0 ? _b : []).map((t) => t.value)),
                ...(((_d = (_c = p.metadata) === null || _c === void 0 ? void 0 : _c.descriptions) !== null && _d !== void 0 ? _d : []).map((d) => d.value)),
                ...((_e = p.keywords) !== null && _e !== void 0 ? _e : []),
                ...(((_g = (_f = p.metadata) === null || _f === void 0 ? void 0 : _f.keywords) !== null && _g !== void 0 ? _g : []).map((k) => k.value)),
            ].filter(Boolean);
            return haystacks.some((h) => h.toLowerCase().includes(q));
        });
    }, [query]);
    // If the current selection is filtered out, clear it
    useEffect(() => {
        if (selected && !filtered.some((p) => p.id === selected.id)) {
            setSelected(null);
        }
    }, [filtered, selected]);
    const handleSelect = (item) => {
        console.log('Selected item:', item);
        setSelected(item);
    };
    const renderDetail = (item) => {
        var _a, _b, _c, _d;
        const keywordSet = new Set();
        ((_a = item.keywords) !== null && _a !== void 0 ? _a : []).forEach((kw) => keywordSet.add(kw));
        ((_c = (_b = item.metadata) === null || _b === void 0 ? void 0 : _b.keywords) !== null && _c !== void 0 ? _c : []).forEach((k) => {
            k.value
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean)
                .forEach((kw) => keywordSet.add(kw));
        });
        const pricingPages = item.pricingPages || [];
        const allKeywords = Array.from(keywordSet);
        return (_jsxs(Stack, { spacing: 2, children: [_jsx(Typography, { variant: "h5", children: item.name }), item.logo && (_jsx(Avatar, { src: item.logo, alt: item.name, sx: { width: 56, height: 56 }, variant: "rounded" })), _jsx(Typography, { variant: "subtitle1", color: "text.secondary", children: item.title }), _jsx(Divider, {}), _jsxs(Stack, { spacing: 0.5, children: [_jsxs(Typography, { variant: "body2", children: ["Hostname: ", item.hostname] }), _jsxs(Typography, { variant: "body2", children: ["Last crawled: ", new Date(item.lastCrawledAt).toLocaleString()] }), _jsx(MuiLink, { href: item.homepage, target: "_blank", rel: "noopener noreferrer", children: "Visit homepage" })] }), pricingPages.length > 0 && (_jsxs(Stack, { spacing: 1, children: [_jsx(Typography, { variant: "subtitle1", children: "Pricing Pages" }), _jsx(Stack, { spacing: 0.5, children: pricingPages.map((page) => {
                                var _a;
                                let label = page.url;
                                try {
                                    const u = new URL(page.url);
                                    const firstSegment = u.pathname
                                        .split('/')
                                        .filter(Boolean)[0] || '';
                                    const lastSegment = u.pathname
                                        .split('/')
                                        .filter(Boolean)
                                        .slice(-1)[0] || '';
                                    const hashPart = u.hash ? ` ${u.hash}` : '';
                                    if (firstSegment === lastSegment) {
                                        label = `/${firstSegment}${hashPart}`;
                                    }
                                    else {
                                        label = `/${firstSegment}...${lastSegment} ${hashPart}`;
                                    }
                                    const queryPart = u.search ? ` ${u.search}` : '';
                                    if (queryPart) {
                                        label += queryPart;
                                    }
                                }
                                catch (_b) {
                                    // fallback to full URL label
                                }
                                const prob = (_a = page.probability) !== null && _a !== void 0 ? _a : 0;
                                let color = 'default';
                                if (prob >= 0.99)
                                    color = 'success';
                                else if (prob >= 0.95)
                                    color = 'warning';
                                else
                                    color = 'error';
                                return (_jsxs(Stack, { direction: "row", spacing: 1, alignItems: "center", children: [_jsx(MuiLink, { href: page.url, target: "_blank", rel: "noopener noreferrer", children: label }), _jsx(Chip, { label: prob.toFixed(3), size: "small", color: color, variant: "outlined" })] }, page.url));
                            }) })] })), allKeywords.length > 0 && (_jsxs(Stack, { spacing: 1, children: [_jsx(Typography, { variant: "subtitle2", children: "Keywords" }), _jsx(Stack, { direction: "row", spacing: 1, sx: { flexWrap: 'wrap' }, children: allKeywords.map((kw) => (_jsx(Chip, { label: kw, size: "small", sx: { mb: 1 } }, kw))) })] })), ((_d = item.metadata) === null || _d === void 0 ? void 0 : _d.descriptions) && item.metadata.descriptions.length > 0 && (_jsxs(Stack, { spacing: 1, children: [_jsx(Typography, { variant: "subtitle2", children: "Descriptions" }), item.metadata.descriptions.map((d) => (_jsx(Typography, { variant: "body2", color: "text.secondary", children: d.value }, d.key)))] }))] }));
    };
    return (_jsxs("div", { style: {
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
        }, children: [_jsx(Typography, { variant: "h4", children: "ProxyGoGo" }), _jsx(TextField, { label: "Search by name, hostname, or title", variant: "outlined", value: query, onChange: (e) => setQuery(e.target.value), fullWidth: true }), _jsxs(Typography, { variant: "subtitle1", children: ["Showing ", filtered.length, " of ", proxies.length, " providers"] }), _jsxs(Stack, { spacing: 3, sx: { p: 2 }, style: {
                    margin: 0,
                    padding: 0,
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    overflow: 'hidden',
                }, children: [_jsx("div", { style: {
                            display: 'flex',
                            height: "100%",
                            width: "50%",
                            overflow: 'scroll',
                        }, children: _jsx(Grid, { container: true, spacing: 2, style: {}, children: filtered.map((p) => (_jsx(Grid, { size: { xs: 12, md: 6, lg: 4 }, children: _jsx(Card, { variant: "outlined", sx: {
                                        height: '100%',
                                        cursor: 'pointer',
                                        borderColor: (selected === null || selected === void 0 ? void 0 : selected.id) === p.id ? 'primary.main' : 'divider',
                                        boxShadow: (selected === null || selected === void 0 ? void 0 : selected.id) === p.id ? 3 : 0,
                                    }, onClick: () => handleSelect(p), children: _jsxs(CardContent, { children: [_jsxs(Stack, { direction: "row", spacing: 1, alignItems: "center", mb: 1, children: [p.logo && (_jsx(Avatar, { src: p.logo, alt: p.name, sx: { width: 32, height: 32 }, variant: "rounded" })), _jsx(Typography, { variant: "h6", children: p.name })] }), _jsx(Typography, { variant: "subtitle2", color: "text.secondary", gutterBottom: true, children: p.title }), _jsxs(Typography, { variant: "body2", gutterBottom: true, children: ["Hostname: ", p.hostname] }), _jsxs(Typography, { variant: "body2", gutterBottom: true, children: ["Last crawled: ", new Date(p.lastCrawledAt).toLocaleString()] }), _jsx(MuiLink, { href: p.homepage, target: "_blank", rel: "noopener noreferrer", children: "Visit homepage" }), (() => {
                                                var _a, _b, _c;
                                                const keywordSet = new Set();
                                                ((_a = p.keywords) !== null && _a !== void 0 ? _a : []).forEach((kw) => keywordSet.add(kw));
                                                ((_c = (_b = p.metadata) === null || _b === void 0 ? void 0 : _b.keywords) !== null && _c !== void 0 ? _c : []).forEach((k) => {
                                                    k.value
                                                        .split(',')
                                                        .map((s) => s.trim())
                                                        .filter(Boolean)
                                                        .forEach((kw) => keywordSet.add(kw));
                                                });
                                                const allKeywords = Array.from(keywordSet);
                                                if (!allKeywords.length)
                                                    return [];
                                                return (_jsx(Stack, { direction: "row", spacing: 1, sx: { flexWrap: 'wrap', mt: 1 }, children: allKeywords.map((kw) => (_jsx(Chip, { label: kw, size: "small", sx: { mb: 1 } }, kw))) }));
                                            })()] }) }) }))) }) }), selected && (_jsx("div", { style: {
                            width: isMobile ? '100%' : '50%',
                            height: "100%",
                            overflow: 'scroll',
                        }, children: _jsx(Card, { variant: "outlined", sx: {
                                p: 2,
                            }, children: _jsx(CardContent, { children: _jsxs(Stack, { spacing: 2, children: [_jsx(Button, { color: "error", size: "small", onClick: () => setSelected(null), sx: {
                                                alignSelf: 'flex-start'
                                            }, children: "Close" }), renderDetail(selected)] }) }) }) }))] })] }));
};
